import { withNamespaces } from "react-i18next"
import Accordion from 'react-bootstrap/Accordion';
import { Col, Row, Table, UncontrolledTooltip } from "reactstrap";
import { useState } from "react";
import DateUtils from "src/services/utils/DateUtils";
import  { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import { RiFileChartLine, RiInformationLine } from "react-icons/ri";

const LocalActivityCodeTypes = {
    "1": "HIGH LEVEL ACTIVITY CODE",
    "10": "NAF (NOMENCLATURE D’ACTIVITE FRANCAISE)",
    "15": "NAF (NOMENCLATURE D’ACTIVITE FRANCAISE) VER2",
    "20": "BELGIUM NACE CODE",
    "25": "BELGIUM NACE CODE 2008",
    "40": "NETHERLAND KVK ACTIVITY CODE",
    "45": "ITALY NACE CODE",
    "50": "SBA CODE",
    "60": "1972 SIC CODE",
    "62": "ISIC",
    "70": "1987 SIC CODE",
    "80": "US GOVT 1977 SIC (MODIFIED IN 1981)",
    "90": "CANADIAN GOVERNMENT SIC",
    "95": "NORWEGIAN NACE CODE",
    "97": "NORWEGIAN NACE CODE REV2",
    "100": "MODERATE LEVEL ACTIVITY CODE",
    "110": "US D&B SIC PLUS 2",
    "112": "NORTH AMERICAN INDUSTRIAL CLSFN SYSTEM",
    "120": "CANADIAN D&B SIC PLUS 4",
    "130": "CANADIAN GOVERNMENT SIC PLUS 4",
    "140": "US GOVERNMENT 2 DIGIT 1977 SIC CODE",
    "150": "ASIA PACIFIC SIC CODE",
    "160": "FINNISH NACE CODE",
    "170": "FINNISH NACE CODE REV2",
    "180": "LUXEMBOURG NACE",
    "200": "DETAILED LEVEL ACTIVITY CODE",
    "210": "US D&B SIC 2 PLUS 2",
    "250": "DNB HYLDAHL NACE",
    "260": "UK FIVE DIGIT SIC",
    "300": "EUROPEAN NACE CODE",
    "305": "US GOVERNMENT 3 DIGIT 1977 SIC CODE",
    "310": "3 DIGIT 1987 SIC CODE",
    "315": "2 DIGIT 1987 SIC CODE",
    "320": "SWEDEN NACE CODE",
    "340": "UK Standard Industry Code 2007",
    "350": "NACE REV1",
    "360": "NACE REVISION 2",
    "370": "SWEDEN NACE CODE REV2",
    "380": "NETHERLANDS SBI CODE 2008",
    "390": "DANISH AUTHORITY NACE REV2",
    "450": "NETHERLANDS NACE CODE"
}

const dateUtils = new DateUtils();

function convertApiDate(dateStr) {
    return dateUtils.convertDateToDate(dateStr, INTERNATIONAL_DATE_FORMAT)
}

const groupSummary = (summary, t) => {
    return [
        {
            "year": summary.statementDate,
            "sections": [
                {
                    "section": "Business Activity Codes",
                    "fields": [
                        {
                            "title": "SIC Activity Codes",
                            "value": (summary.sicActivityCodes || []).map((aCode) => {
                                return `${aCode.code} - ${aCode.description}`
                            }),
                            "description": ""
                        },
                        {
                            "title": "SIC8 Activity Codes",
                            "value": summary.sic8ActivityCodes,
                            "description": ""
                        },
                        {
                            "title": "Local Activity Code",
                            "value": summary.localActivityCode,
                            "description": ""
                        },
                        {
                            "title": "Local Activity Code Type",
                            "value": `${summary.localActivityCodeType} (${LocalActivityCodeTypes[summary.localActivityCodeType]})`,
                            "description": "The type of classification system used for the Local Activity Code."
                        }
                    ]
                },
                {
                    "section": "Financial Summary",
                    "fields": [
                        {
                            "title": "Annual Sales (Local Currency)",
                            "value": Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "EUR",
                                maximumFractionDigits: 2,
                            }).format(Number(summary.annualSales)),
                            "description": "Total revenue in local currency attributable to products and services sold annually."
                        },
                        {
                            "title": "Annual Sales (USD)",
                            "value": Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                                maximumFractionDigits: 2,
                            }).format(Number(summary.annualSalesUs)),
                            "description": ""
                        },
                        {
                            "title": "Currency",
                            "value": summary.currency,
                            "description": ""
                        },
                        {
                            "title": "Net Worth (Local Currency)",
                            "value": Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "EUR",
                                maximumFractionDigits: 2,
                            }).format(Number(summary.netWorth)),
                            "description": "Total revenue in local currency attributable to products and services sold annually."
                        },
                        {
                            "title": "Net Worth (USD)",
                            "value": Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                                maximumFractionDigits: 2,
                            }).format(Number(summary.netWorthUs)),
                            "description": ""
                        },
                        {
                            "title": "Net Income (Local Currency)",
                            "value": Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "EUR",
                                maximumFractionDigits: 2,
                            }).format(Number(summary.netIncome)),
                            "description": ""
                        },
                        {
                            "title": "Net Income (USD)",
                            "value": Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                                maximumFractionDigits: 2,
                            }).format(Number(summary.netIncomeUs)),
                            "description": ""
                        }
                    ]
                },
                {
                    "section": "Workforce",
                    "fields": [
                        {
                            "title": "Number of Employees",
                            "value": summary.employees,
                            "description": ""
                        },
                        {
                            "title": "Total Employees",
                            "value": summary.employeesTotal,
                            "description": ""
                        }
                    ]
                },
                {
                    "section": "Statement Metadata",
                    "fields": [
                        {
                            "title": "Start Year",
                            "value": summary.startYear,
                            "description": ""
                        },
                        {
                            "title": "Statement Date",
                            "value": convertApiDate(summary.statementDate),
                            "description": ""
                        },
                        {
                            "title": "Report Date",
                            "value": convertApiDate(summary.reportDate),
                            "description": ""
                        },
                        {
                            "title": "Out of Business",
                            "value": summary.outOfBusiness ? t('Yes') : t('No'),
                            "description": ""
                        },
                        {
                            "title": "Deleted Record",
                            "value": summary.deleted ? t('Yes') : t('No'),
                            "description": ""
                        }
                    ]
                }
            ]
        }
    ]
}

const FinancialSummary = ({
    t,
    activeAccordion, 
    setActiveAccordion,

    infoQuery
}) => {
    const [showMore, setShowMore] = useState(false);

    if (infoQuery.isFetching) {
        return (
            <Row>
                <Col 
                    sm="12" 
                    className="mt-4 mb-4"
                >
                    <div 
                        className="dt-field dt-skeleton" 
                        style={{ width: '100%', height: '100px' }}
                    />
                </Col>
            </Row>
        )
    }

    const financialData = groupSummary(infoQuery.data.financial_summary, t)

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('financial-summary')}
            className={activeAccordion === 'financial-summary' ? 'active' : ''}
            eventKey={'financial-summary'}
        >
            <Accordion.Header>
                <RiFileChartLine size={24} className="me-2"/>
                <span>
                    {t('Financial Summary')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                <Row>
                    <Col sm="12">
                        <div
                            id="partner-financial-content"
                            style={{
                                overflow: showMore ? 'visible' : 'hidden',
                                maxHeight: showMore ? '100%' : '600px'
                            }}
                        >
                            <Table striped responsive size="md" inverse>
                                <thead>
                                    <tr className="table-light">
                                        <th scope="col" style={{ width: '25%' }}>
                                            {`${t('Metric')} / ${t('Description')}`}
                                        </th>
                                        {financialData.map((yearData) => {
                                            return (
                                                <th scope="col" style={{
                                                    width: `${75 / financialData.length}%`
                                                }}>
                                                    {dateUtils.convertDateToDate(yearData.year, INTERNATIONAL_DATE_FORMAT)}
                                                </th>
                                            )
                                        })}
                                    </tr>
                                </thead>

                                {financialData[0].sections.map((section, sectionIndex) => {
                                    return (
                                        <tbody key={sectionIndex}>
                                            <tr>
                                                <th className="text-center" colSpan={financialData.length + 1}>
                                                    {t(section.section)}
                                                </th>
                                            </tr>
                                            {section.fields.map((field, fieldIndex) => {
                                                return (
                                                    <tr key={sectionIndex + fieldIndex}>
                                                        <th>
                                                            {t(field.title)}
                                                            {field.description ? (
                                                                <>
                                                                    <span 
                                                                        id={`tooltip-${sectionIndex}-${fieldIndex}`} 
                                                                        className="ms-1" 
                                                                        style={{ cursor: "pointer" }}>
                                                                        <RiInformationLine />
                                                                    </span>
                                                                    <UncontrolledTooltip 
                                                                        target={`tooltip-${sectionIndex}-${fieldIndex}`}
                                                                    >
                                                                        {t(field.description)}
                                                                    </UncontrolledTooltip>
                                                                </>
                                                            ) : null}
                                                        </th>
                                                        {financialData.map((eachFieldYear, yearIndex) => {
                                                            const val = eachFieldYear.sections[sectionIndex].fields[fieldIndex].value;

                                                            return (
                                                                <td key={sectionIndex + fieldIndex + yearIndex}>
                                                                    {val}
                                                                </td>
                                                            )
                                                        })}
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    )
                                })}
                            </Table>
                        </div>

                        <div
                            className="show-more text-center font-size-16 text-primary mt-2"
                            onClick={() => setShowMore(!showMore)}
                        >
                            <i className="ri-arrow-down-s-line font-size-22 align-middle" />
                            <span>
                                {t(showMore ? 'View less' : 'View more')}
                            </span>
                        </div>
                    </Col>
                </Row>
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    FinancialSummary
)